import React from 'react';
import { ArrowUpRight } from 'lucide-react';
import Navbar from '../components/site/Navbar';
import Blog from '../components/site/Blog';
import FooterCTA from '../components/site/FooterCTA';
import Footer from '../components/site/Footer';
import LogoWatermark from '../components/site/LogoWatermark';
import { aboutHeroImages, stats, awards, whatWeDo } from '../data/mock';

const Pill = ({ children, light }) => (
  <span
    className="inline-block px-4 py-1.5 rounded-md font-display uppercase tracking-wider text-sm md:text-base"
    style={{ background: '#2EF5FF', color: '#12367F' }}
  >
    {children}
  </span>
);

const AboutHero = () => (
  <section className="relative pt-28 md:pt-32 overflow-hidden" style={{ background: '#12367F', color: '#FFFFFF' }}>
    <LogoWatermark size={500} opacity={0.07} position="bottom-right" />
    <div className="container-x px-6 pt-12 md:pt-20 pb-16 md:pb-24 relative">
      <div className="grid lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-7">
          <Pill>Who we are</Pill>
          <h1
            className="mt-8 font-display uppercase text-balance"
            style={{ fontSize: 'clamp(3rem, 8.5vw, 9rem)', lineHeight: 0.88 }}
          >
            <span className="text-white">The People</span>
            <br />
            <span style={{ color: '#2EF5FF' }}>Connecting</span>
            <br />
            <span style={{ color: '#2EF5FF' }}>Brands &amp; Creators</span>
          </h1>
        </div>
        <div className="lg:col-span-5">
          <div className="grid grid-cols-3 gap-3 md:gap-4">
            {aboutHeroImages.slice(0, 9).map((src, i) => (
              <div
                key={i}
                className="relative rounded-2xl overflow-hidden bg-white/10"
                style={{
                  aspectRatio: i % 4 === 0 ? '3 / 5' : '3 / 4',
                  transform: i % 2 === 0 ? 'translateY(0)' : 'translateY(18px)'
                }}
              >
                <img
                  src={src}
                  alt=""
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </section>
);

const LifeAtConnect = () => (
  <section className="section" style={{ background: '#FFFFFF' }}>
    <div className="container-x">
      <Pill>Life at Nasu Group</Pill>
      <div className="mt-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
        <div className="lg:col-span-7">
          <h2
            className="font-display uppercase text-[#12367F] text-balance"
            style={{ fontSize: 'clamp(2.25rem, 5.5vw, 5rem)', lineHeight: 0.92 }}
          >
            Where the culture is contagious,
            <br />
            the energy is infectious &amp;
            <br />
            <span style={{ background: '#2EF5FF' }} className="px-2 rounded-md">the campaigns go viral</span>
          </h2>
        </div>
        <div className="lg:col-span-5 space-y-5 text-[#12367F]/85 text-base md:text-lg leading-relaxed">
          <p>
            It started in 2020 when we saw something was missing. If celebrities had talent agents,
            why shouldn&apos;t social-first creators? We knew talent was being held back because
            they had too much to manage. Admin. Contracts. Negotiations. They didn&apos;t have the
            time to simply do what they love &mdash; create content.
          </p>
          <p>
            So we fixed it. And, in 5 years, we went from a startup with 10 creators on our books
            to a full-scale talent agency with 150+ social-first creators. Today, we give creators
            a new kind of freedom. They&apos;re free to create content. Free to flex their
            creativity. Free to design their future and earn a living from their passion.
          </p>
          <p>
            And, by sticking to what we&apos;re good at, we&apos;ve become one of the
            fastest-growing social-first agencies across Both UK and USA, taking brands and talent
            to the top with us. Come along to create a future you&apos;re obsessed with.
          </p>
          <div className="pt-4">
            <a href="/contact" className="btn-base btn-accent">
              Work with us <ArrowUpRight className="h-4 w-4" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const Stats = () => (
  <section className="pb-20 md:pb-28" style={{ background: '#FFFFFF' }}>
    <div className="container-x px-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-12">
        {stats.map((s) => (
          <div key={s.label}>
            <p className="font-display uppercase text-[#12367F]/85 text-xl md:text-2xl pb-3 border-b-2 border-[#12367F]/20">
              {s.label}
            </p>
            <p
              className="mt-8 font-display text-[#12367F]"
              style={{ fontSize: 'clamp(3.5rem, 8vw, 8rem)', lineHeight: 0.9 }}
            >
              {s.value}
            </p>
            <p className="mt-6 text-[#12367F]/75 text-sm md:text-base leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const AwardCard = ({ award }) => (
  <div className="shrink-0 w-[220px] md:w-[260px] aspect-[3/4] rounded-3xl border border-white/10 bg-white/[0.04] grid place-items-center p-8 hover:bg-white/[0.08] transition-colors whitespace-normal">
    <div className="text-center">
      <p className="font-display uppercase text-white text-xl md:text-2xl leading-tight whitespace-normal">{award.tag}</p>
      <div className="mt-4 h-px w-12 bg-[#2EF5FF] mx-auto" />
      <p className="mt-4 text-white/60 text-xs md:text-sm uppercase tracking-wider whitespace-normal">
        {award.name.replace(award.tag + ' \u2014 ', '')}
      </p>
    </div>
  </div>
);

const Awards = () => {
  const items = [...awards, ...awards];
  return (
    <section className="section relative overflow-hidden" style={{ background: '#12367F', color: '#FFFFFF' }}>
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]" style={{
        backgroundImage: 'radial-gradient(#2EF5FF 1.5px, transparent 1.5px)',
        backgroundSize: '28px 28px'
      }} />
      <LogoWatermark size={480} opacity={0.06} position="left" />
      <div className="container-x relative">
        <div className="text-center">
          <Pill>Our Company Awards</Pill>
          <h2
            className="mt-8 font-display uppercase text-balance max-w-5xl mx-auto"
            style={{ fontSize: 'clamp(2rem, 4.5vw, 4.25rem)', lineHeight: 1 }}
          >
            <span className="text-white">We&apos;re proud to be recognised for</span>
            <br />
            <span style={{ background: '#2EF5FF', color: '#12367F' }} className="inline-block px-3 py-1 rounded-lg mt-3">
              Our exceptional company culture
            </span>
          </h2>
        </div>
      </div>
      <div className="mt-16 md:mt-20 overflow-hidden">
        <div className="flex gap-5 md:gap-6 animate-marquee-slow whitespace-nowrap will-change-transform" style={{ width: 'max-content' }}>
          {items.map((a, i) => (
            <AwardCard key={i} award={a} />
          ))}
        </div>
      </div>
    </section>
  );
};

const WhatWeDo = () => (
  <section className="section" style={{ background: '#FFFFFF' }}>
    <div className="container-x">
      <div className="text-center">
        <Pill>What we do</Pill>
        <h2
          className="mt-8 font-display uppercase text-[#12367F] text-balance max-w-5xl mx-auto"
          style={{ fontSize: 'clamp(2.25rem, 5.5vw, 5rem)', lineHeight: 0.92 }}
        >
          Opening doors and changing
          <br />
          lives for brands and creators
        </h2>
      </div>

      <div className="mt-16 md:mt-24 space-y-20 md:space-y-28">
        {whatWeDo.map((item, i) => {
          const reverse = i % 2 === 1;
          return (
            <div key={i} className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
              <div className={`lg:col-span-6 ${reverse ? 'lg:order-2' : ''}`}>
                <div className="relative rounded-3xl overflow-hidden aspect-[4/5] bg-[#12367F]/10">
                  <img src={item.image} alt={item.title} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
                </div>
              </div>
              <div className={`lg:col-span-6 ${reverse ? 'lg:order-1' : ''}`}>
                <h3
                  className="font-display uppercase text-[#12367F]"
                  style={{ fontSize: 'clamp(2rem, 4.5vw, 4rem)', lineHeight: 0.95 }}
                >
                  {item.title}
                </h3>
                <div className="mt-6 space-y-5 text-[#12367F]/85 text-base md:text-lg leading-relaxed">
                  {item.body.map((p, j) => <p key={j}>{p}</p>)}
                </div>
                <div className="mt-8">
                  <a href={item.cta.href} className="btn-base btn-accent">
                    {item.cta.label} <ArrowUpRight className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const About = () => {
  return (
    <main className="bg-white text-[#12367F]">
      <Navbar />
      <AboutHero />
      <LifeAtConnect />
      <Stats />
      <Awards />
      <WhatWeDo />
      <Blog />
      <FooterCTA />
      <Footer />
    </main>
  );
};

export default About;
